/**
 * ============================================================================
 *  File        : frontend/src/api/freee.ts
 *  Module      : api
 *  Purpose     : Freee SLA（計時 + 提醒事件，不串接 Freee API）+ financial_documents
 *                型別化 API（v1.3-11；spec §10.3 + §B4 + CLAUDE.md §0）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http/src/routes/v13_freee.rs，掛在 v1：
 *    GET  /api/v1/cases/:case_id/freee-tasks                -> list（marketplace.freee_sla.view）
 *    POST /api/v1/cases/:case_id/freee-tasks/:id/complete   -> complete（marketplace.freee_sla.complete）
 *    GET  /api/v1/cases/:case_id/financial-documents        -> list（marketplace.financial_documents.view）
 *    POST /api/v1/cases/:case_id/financial-documents        -> record（marketplace.financial_documents.create）
 *
 *  **Freee API は呼ばない**（CLAUDE.md §0）：freee_task は 3 営業日 SLA の計時のみ;
 *  financial-documents は freee 管理番号 / 請求書番号 / 仕切書番号 / 附件参照の記録のみ。
 */

import {
  API_BASE,
  mutationHeaders,
  requestData,
  requestEnvelope,
  type Envelope,
} from './client'
import type { ApiSchemas } from './client'

/** Freee SLA タイマー task DTO（= 後端 FreeeTaskDto）。 */
export type FreeeTaskDto = ApiSchemas['FreeeTaskDto']
/** 財務書類参照 DTO（= 後端 FinancialDocumentDto）。 */
export type FinancialDocumentDto = ApiSchemas['FinancialDocumentDto']
/** financial-documents 記録 request body（= 後端 RecordFinancialDocumentDto）。 */
export type RecordFinancialDocumentDto = ApiSchemas['RecordFinancialDocumentDto']

const CASES_PATH = `${API_BASE}/cases`

/** 列出某案件的所有 Freee SLA task（最新在前）。 */
export function listFreeeTasks(
  caseId: number | string,
): Promise<Envelope<FreeeTaskDto[]>> {
  return requestEnvelope<FreeeTaskDto[]>(`${CASES_PATH}/${caseId}/freee-tasks`, {
    method: 'GET',
  })
}

/** 將 Freee task 標記 completed（請求書/仕切書 を実際に発行済とマーク）。 */
export function completeFreeeTask(
  caseId: number | string,
  taskId: number | string,
): Promise<FreeeTaskDto> {
  return requestData<FreeeTaskDto>(
    `${CASES_PATH}/${caseId}/freee-tasks/${taskId}/complete`,
    { method: 'POST', headers: mutationHeaders() },
  )
}

/** 列出某案件的所有財務書類参照（最新在前）。 */
export function listFinancialDocuments(
  caseId: number | string,
): Promise<Envelope<FinancialDocumentDto[]>> {
  return requestEnvelope<FinancialDocumentDto[]>(
    `${CASES_PATH}/${caseId}/financial-documents`,
    { method: 'GET' },
  )
}

/** 記録一筆財務書類参照（freee 管理番号 / 請求書番号 / 仕切書番号 / 附件）。 */
export function recordFinancialDocument(
  caseId: number | string,
  body: RecordFinancialDocumentDto,
): Promise<FinancialDocumentDto> {
  return requestData<FinancialDocumentDto>(
    `${CASES_PATH}/${caseId}/financial-documents`,
    { method: 'POST', body, headers: mutationHeaders() },
  )
}

export type { Envelope }
